// lib/apiClient.ts
import axios, { AxiosError, AxiosResponse } from 'axios'
import { toast } from 'sonner'
import i18n from '@/utils/i18n'
import { getToken, setToken, removeTokens, getRefreshToken, setRefreshToken } from '@/utils/auth'

// Çağıran tarafın global error toast'unu bastırması için axios config'e
// `skipErrorToast: true` eklenebilsin diye axios tipini genişletiyoruz.
declare module 'axios' {
    export interface AxiosRequestConfig {
        skipErrorToast?: boolean
    }
    export interface InternalAxiosRequestConfig {
        skipErrorToast?: boolean
    }
}

/** Dizi parametreleri tekrarlayan key olarak serialize eder: instructorIds=id1&instructorIds=id2 ([] yok).
 * Number 0 değerleri (örn. status=0) korunur — sadece undefined/null/"" filtrelenir.
 * Axios 1.x object-form beklediği için `{ serialize }` şeklinde sarıyoruz;
 * eski function-form deprecation warning atıyor ve bazı patch sürümlerinde
 * düşebiliyor. */
function serializeParams(params: unknown): string {
    if (!params || typeof params !== "object" || Array.isArray(params)) return ""
    const search = new URLSearchParams()
    Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return
        if (Array.isArray(value)) {
            value.forEach((v) => {
                if (v === undefined || v === null || v === "") return
                search.append(key, String(v))
            })
        } else {
            search.append(key, String(value))
        }
    })
    return search.toString()
}

export const apiClient = axios.create({
    baseURL: import.meta.env.VITE_API_URL || "http://localhost:3000",
    headers: {
        Accept: "application/json",
        "Accept-Language": localStorage.getItem("i18nextLng") || "tr",
        "Content-Type": "application/json",
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
    },
    paramsSerializer: { serialize: serializeParams },
})

// Request interceptor
apiClient.interceptors.request.use(
    (config) => {
        // Refresh endpoint @Public - geçersiz token göndermeyi önle
        const isRefreshRequest = String(config.url || "").includes("/auth/refresh")
        if (!isRefreshRequest) {
            const token = getToken()
            if (token) {
                config.headers["Authorization"] = `Bearer ${token}`
            }
        }

        // Update Accept-Language header dynamically from localStorage
        const currentLanguage = localStorage.getItem("i18nextLng")
        if (currentLanguage) {
            config.headers["Accept-Language"] = currentLanguage
        }

        // Remove Content-Type header for FormData to let axios set it with boundary automatically
        if (config.data instanceof FormData) {
            delete config.headers["Content-Type"]
        }

        // Log API calls in development
        if (import.meta.env.DEV) {
            console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
                baseURL: config.baseURL,
                data: config.data,
                params: config.params
            })
        }

        return config
    },
    (error) => Promise.reject(error)
)

// Response interceptor
apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
        if (import.meta.env.DEV) {
            console.log(`✅ API Response: ${response.config?.method?.toUpperCase()} ${response.config?.url}`, {
                status: response.status,
                data: response.data
            })
        }

        // Backend (euas-backend) response: { success, data: { accessToken, refreshToken, user, expiresIn } }
        let token = null
        if (response.data?.data?.accessToken) {
            token = response.data.data.accessToken
        }
        else if (response.data?.data?.auth?.accessToken) {
            token = response.data.data.auth.accessToken
        }
        else if (response.data?.accessToken) {
            token = response.data.accessToken
        }
        else if (response.data?.data?.token) {
            token = response.data.data.token
        }
        else if (response.data?.token) {
            token = response.data.token
        }

        if (token && !setToken(token)) {
            console.error("Failed to set token")
        }

        // Backend refresh token: data.refreshToken
        let refreshToken = null
        if (response.data?.data?.refreshToken) {
            refreshToken = response.data.data.refreshToken
        }
        else if (response.data?.data?.auth?.refreshToken) {
            refreshToken = response.data.data.auth.refreshToken
        }
        else if (response.data?.refreshToken) {
            refreshToken = response.data.refreshToken
        }
        else if (response.data?.data?.refresh_token) {
            refreshToken = response.data.data.refresh_token
        }
        else if (response.data?.refresh_token) {
            refreshToken = response.data.refresh_token
        }

        if (refreshToken && !setRefreshToken(refreshToken)) {
            console.error("Failed to set refresh token")
        }

        return response
    },
    async (error: AxiosError) => {
        const originalRequest = error.config as { _retry?: boolean; url?: string; headers?: Record<string, string> }
        const isLoginPage = window.location.pathname.includes("/login")

        const errorMessage = (error?.response?.data as { message?: string; error?: string })?.message ||
            (error?.response?.data as { message?: string; error?: string })?.error ||
            i18n.t("apiClient.unexpectedError")

        if (import.meta.env.DEV) {
            console.error(`❌ API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
                status: error.response?.status,
                message: errorMessage,
                data: error.response?.data
            })
        }

        // Kimlik doğrulama uçlarında (login/ldap, login/ldap-local) 401 =
        // "geçersiz kimlik bilgisi", oturum süresinin dolması DEĞİL. Bu uçlarda token
        // refresh denemek anlamsızdır; üstelik geçersiz girişin ardından (eski bir
        // refresh token localStorage'da kalmışsa) orijinal login isteğini tekrar
        // oynatıp rate-limit bütçesini 2 katına çıkarır → kullanıcı 5 yerine 2-3
        // denemede kilitlenir. Bu yüzden auth uçlarında refresh akışını atlayıp
        // hatayı doğrudan çağıran bileşene bırakıyoruz. ('login' KALMALI; OTP /
        // şifre sıfırlama uçları pentest 3.3.3 ile kapatıldığı için listeden çıktı.)
        const reqUrl = String(originalRequest?.url || "")
        const isAuthCredentialRequest = /\/auth\/login/.test(reqUrl)

        if (error.response && error.response.status === 401 && !originalRequest._retry && !isAuthCredentialRequest) {
            // Refresh endpoint'in kendisi 401 döndüyse tekrar deneme (sonsuz döngü önleme)
            const requestUrl = reqUrl
            const isRefreshRequest = String(requestUrl).includes("/auth/refresh")

            if (isRefreshRequest) {
                removeTokens()
                if (!isLoginPage) {
                    window.location.href = "/login"
                }
                return Promise.reject(error)
            }

            originalRequest._retry = true
            const refreshToken = getRefreshToken()

            if (!refreshToken) {
                removeTokens()
                if (!isLoginPage) {
                    window.location.href = "/login"
                }
                return Promise.reject(error)
            }

            try {
                // Swagger: POST /api/auth/refresh body: { refreshToken: string }
                const { data } = await apiClient.post("/api/auth/refresh", {
                    refreshToken,
                })

                // Backend response: { success, data: { accessToken, refreshToken, expiresIn } }
                const newToken = data?.data?.accessToken || data?.accessToken
                const newRefreshToken = data?.data?.refreshToken || data?.refreshToken

                if (newToken && setToken(newToken)) {
                    if (newRefreshToken) {
                        setRefreshToken(newRefreshToken) // Token rotation - yeni refresh token'ı kaydet
                    }
                    originalRequest.headers = originalRequest.headers || {}
                    originalRequest.headers["Authorization"] = `Bearer ${newToken}`
                    return apiClient(originalRequest)
                } else {
                    throw new Error("Failed to set new token")
                }
            } catch (refreshError) {
                removeTokens()
                if (!isLoginPage) {
                    window.location.href = "/login"
                }
                return Promise.reject(refreshError)
            }
        }

        // Backend'den gelen hataları toast ile göster — ancak çağıran taraf
        // kendi catch bloğunda özelleştirilmiş bir toast göstermek istiyorsa
        // request config'e `skipErrorToast: true` koyarak interceptor'ı
        // susturabilir (mükerrer toast'ı engeller).
        const cfg = error.config as { skipErrorToast?: boolean } | undefined
        if (!cfg?.skipErrorToast) {
            toast.error(errorMessage)
        }
        return Promise.reject(error)
    }
)

export default apiClient
